// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_AVATAR_IMAGE_BYTES,
  validateImageFile,
} from "../utils/imageUploadValidation";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("Failed to load image")));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedBlob = async (imageSrc, cropPixels, mimeType) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const size = 512;

  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const sx = cropPixels.x * scaleX;
  const sy = cropPixels.y * scaleY;
  const sWidth = cropPixels.width * scaleX;
  const sHeight = cropPixels.height * scaleY;

  ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, size, size);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      mimeType,
      0.92
    );
  });
};

const resolveCompressionFn = () => {
  if (typeof imageCompression === "function") return imageCompression;
  if (imageCompression && typeof imageCompression.default === "function") return imageCompression.default;
  return null;
};

export default function AvatarEditModal({ isOpen, onClose, onSave }) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setImageSrc("");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropPixels(null);
      setError("");
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file, { maxBytes: MAX_AVATAR_IMAGE_BYTES });
    if (!validation.ok) {
      setError(validation.error || "Please upload a valid image.");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setImageSrc(dataUrl);
      setError("");
    } catch (err) {
      setError("Could not read that file. Try another image.");
    }
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCropPixels(croppedPixels);
  }, []);

  const canSave = useMemo(() => Boolean(imageSrc && cropPixels), [imageSrc, cropPixels]);

  const handleSave = async () => {
    if (!canSave || !cropPixels) return;
    setIsSaving(true);
    setError("");

    try {
      const webpBlob = await getCroppedBlob(imageSrc, cropPixels, "image/webp");
      const rawBlob = webpBlob || await getCroppedBlob(imageSrc, cropPixels, "image/png");

      const compress = resolveCompressionFn();
      const compressed = compress
        ? await compress(rawBlob, {
            maxSizeMB: 0.6,
            maxWidthOrHeight: 512,
            useWebWorker: true,
            fileType: rawBlob.type || "image/webp"
          })
        : rawBlob;

      await onSave(compressed);
    } catch (err) {
      setError(err.message || "Could not save the avatar.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="avatar-modal-backdrop" role="dialog" aria-modal="true">
      <div className="avatar-modal">
        <div className="avatar-modal-header">
          <h3>Edit Profile Photo</h3>
          <button type="button" className="avatar-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="avatar-modal-body">
          {!imageSrc ? (
            <label className="avatar-upload">
              <input
                type="file"
                accept={IMAGE_INPUT_ACCEPT_ATTR}
                onChange={handleFileChange}
                aria-label="Upload profile photo"
              />
              <div className="avatar-upload-card">
                <div className="avatar-upload-title">Upload a square photo</div>
                <div className="avatar-upload-subtitle">PNG, JPG/JPEG, WEBP, AVIF. Max 2MB.</div>
              </div>
            </label>
          ) : (
            <div className="avatar-cropper">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
              <div className="avatar-zoom">
                <label htmlFor="avatar-zoom" className="avatar-zoom-label">Zoom</label>
                <input
                  id="avatar-zoom"
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </div>
            </div>
          )}

          {error && <div className="avatar-error">{error}</div>}
        </div>

        <div className="avatar-modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={!canSave || isSaving}
          >
            {isSaving ? "Saving..." : "Save Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
